import { useEffect, RefObject } from 'react'

const colors = ['#fa6996', '#b08efa', '#4569fc'] as const
type ColorType = (typeof colors)[number]

const frequencies = [1, 1.5, 0.7] // Diferentes frecuencias para cada color

interface GlowConfig {
  amplitude?: number
  secondaryAmplitude?: number
  primaryFreq?: number
  secondaryFreq?: number
  speed?: number
  blur?: number
  opacityPink?: number
  opacityPurple?: number
  opacityBlue?: number
  margin?: number
}

const useGlow = (
  canvasRef: RefObject<HTMLCanvasElement>,
  config: GlowConfig = {},
  inset: boolean = false,
) => {
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    // Configuración por defecto
    const {
      amplitude = 8,
      secondaryAmplitude = 0.15,
      primaryFreq = 16,
      secondaryFreq = 20,
      speed = 3.3,
      blur = 2,
      opacityPink = 0.25,
      opacityPurple = 0.25,
      opacityBlue = 0.33,
      margin = 12,
    } = config

    const { width, height } = canvas.getBoundingClientRect()
    canvas.width = width
    canvas.height = height

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.globalCompositeOperation = 'screen'
    let time = 0
    let animationFrameId: number

    const drawLayer = (
      color: ColorType,
      offset: number,
      alpha: number,
      scale = 1,
      frequencyMod = 1,
    ) => {
      const points = []
      // const safeMargin = margin / scale
      const width = canvas.width / scale
      const height = canvas.height / scale
      const step = 5 // Menor número = más puntos = más suave

      // Para cada posición, usamos diferentes frecuencias para horizontal y vertical
      for (let x = margin; x <= width - margin; x += step) {
        const y =
          margin +
          Math.sin(x / (primaryFreq / frequencyMod) + time + offset) *
            (amplitude / scale) +
          Math.cos(x / (secondaryFreq / frequencyMod) + time * 1.1) *
            ((amplitude * secondaryAmplitude) / scale)
        points.push([x * scale, y * scale])
      }

      for (let y = margin; y <= height - margin; y += step) {
        const x =
          width -
          margin +
          Math.sin(y / (primaryFreq / frequencyMod) + time * 0.9 + offset) *
            (amplitude / scale) +
          Math.cos(y / (secondaryFreq / frequencyMod) + time) *
            ((amplitude * secondaryAmplitude) / scale)
        points.push([x * scale, y * scale])
      }

      for (let x = width - margin; x >= margin; x -= step) {
        const y =
          height -
          margin +
          Math.sin(x / (primaryFreq / frequencyMod) + time * 1.1 + offset) *
            (amplitude / scale) +
          Math.cos(x / (secondaryFreq / frequencyMod) + time * 0.9) *
            ((amplitude * secondaryAmplitude) / scale)
        points.push([x * scale, y * scale])
      }

      for (let y = height - margin; y >= margin; y -= step) {
        const x =
          margin +
          Math.sin(y / (primaryFreq / frequencyMod) + time + offset) *
            (amplitude / scale) +
          Math.cos(y / (secondaryFreq / frequencyMod) + time * 1.1) *
            ((amplitude * secondaryAmplitude) / scale)
        points.push([x * scale, y * scale])
      }

      ctx.beginPath()
      ctx.moveTo(points[0][0], points[0][1])

      // Curvas más suaves usando curvas de Bézier cúbicas
      for (let i = 0; i < points.length - 1; i++) {
        const curr = points[i]
        const next = points[(i + 1) % points.length]
        const nextNext = points[(i + 2) % points.length]

        const cp1x = curr[0] + (next[0] - curr[0]) * 0.5
        const cp1y = curr[1] + (next[1] - curr[1]) * 0.5
        const cp2x = next[0] + (nextNext[0] - next[0]) * 0.5
        const cp2y = next[1] + (nextNext[1] - next[1]) * 0.5

        ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, next[0], next[1])
      }

      ctx.closePath()
      ctx.fillStyle = `rgba(${hexToRgb(color)}, ${alpha})`
      ctx.filter = `blur(${blur * scale}px)`

      if (inset) {
        // Dibujar el rectángulo exterior
        ctx.moveTo(0, 0)
        ctx.lineTo(canvas.width, 0)
        ctx.lineTo(canvas.width, canvas.height)
        ctx.lineTo(0, canvas.height)
        ctx.closePath()
        // Rellenar usando la regla evenodd para invertir
        ctx.fill('evenodd')
      } else {
        // Comportamiento original
        ctx.fill()
      }

      ctx.filter = 'none'
    }

    const hexToRgb = (hex: string): string | null => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
      return result
        ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
        : null
    }

    const animate = () => {
      time += speed / 100
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      const opacities = [opacityPink, opacityPurple, opacityBlue]

      colors.forEach((color, i) => {
        const baseOpacity = opacities[i]
        const freq = frequencies[i]

        drawLayer(color, (i * Math.PI) / 2, baseOpacity * 0.2, 1.15, freq * 0.8)
        drawLayer(
          color,
          (i * Math.PI) / 1.5,
          baseOpacity * 0.3,
          1.1,
          freq * 0.9,
        )
        drawLayer(color, i * Math.PI, baseOpacity * 0.5, 1.05, freq)
        drawLayer(color, i * Math.PI * 1.5, baseOpacity * 0.7, 1.02, freq * 1.1)
        drawLayer(color, i * Math.PI * 2, baseOpacity, 1, freq * 1.2)
      })

      animationFrameId = requestAnimationFrame(animate)
    }

    animate()

    return () => {
      cancelAnimationFrame(animationFrameId)
    }
  }, [canvasRef, config, inset])
}

export default useGlow
